'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createBrowserClient, SupabaseConfigError } from '@/lib/supabase';

type Props = {
  redirectTo?: string;
};

export default function LoginClient({ redirectTo }: Props) {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const supabase = createBrowserClient();
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (authError) {
        if (authError.message.includes('Invalid login')) {
          setError('Email ou senha incorretos');
        } else {
          setError('Erro ao fazer login. Tente novamente.');
        }
        return;
      }

      router.push(redirectTo || '/dashboard');
    } catch (err) {
      console.error('Login error:', err);
      if (err instanceof SupabaseConfigError) {
        setError('Serviço temporariamente indisponível. Tente novamente em alguns minutos.');
      } else {
        setError('Erro de conexão. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">


        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 md:p-8">
          <h1 className="text-xl md:text-2xl font-bold text-white text-center mb-1.5 md:mb-2">Entrar na sua conta</h1>
          <p className="text-gray-400 text-center text-sm md:text-base mb-6 md:mb-8">Acesse seu painel de agentes de IA</p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm text-gray-300 mb-1.5 md:mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="w-full px-4 py-2.5 md:py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm md:text-base placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm text-gray-300 mb-1.5 md:mb-2">
                Senha
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
                className="w-full px-4 py-2.5 md:py-3 bg-white/10 border border-white/20 rounded-xl text-white text-sm md:text-base placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition"
              />
            </div>

            {error && <p className="text-red-400 text-sm text-center">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 md:py-3 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl text-white font-bold text-sm md:text-base hover:opacity-90 transition disabled:opacity-50"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <div className="mt-5 md:mt-6 text-center space-y-2">
            <p className="text-gray-400 text-sm md:text-base">
              Não tem conta?{' '}
              <Link href="/signup" className="text-cyan-400 hover:underline">
                Criar conta grátis
              </Link>
            </p>
            <p className="text-center text-xs md:text-sm text-gray-500">
              <Link href="/reset-password" className="text-cyan-400 hover:underline">
                Esqueceu sua senha?
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center mt-5 md:mt-6">
          <Link href="/" className="text-gray-500 hover:text-gray-300 transition text-sm md:text-base">
            ← Voltar para o início
          </Link>
        </p>
      </motion.div>
    </main>
  );
}
